import { getFeatureFlagValue } from '@citrite/feature-flags';
import { Api } from '@citrite/sf-api';
import { Users } from '@sharefiledev/icons';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { type PiletApi } from '@sharefiledev/sharefile-appshell';
import { addBlocks } from './blocks';
import { addBlockDevelopment } from './development';
import { AssignContactToClientExtension } from './extensions/AssignContactToClient';
import { AssignResourceToClientExtension } from './extensions/AssignResourceToClient';
import { ClientConfigurationDataAccessorExtension } from './extensions/ClientConfigurationDataAccessor';
import { ClientConfigurationPage } from './extensions/ClientConfigurationPage';
import { ClientContactSelectorExtension } from './extensions/ClientContactSelector';
import { AsyncClientManagementConfiguration } from './extensions/ClientManagementConfiguration';
import { ClientManagementGroupDataExtensionSlotComponent } from './extensions/ClientManagementGroupDataProvider';
import { ClientManagementHomePage } from './extensions/ClientManagementHomePage';
import { ClientPrincipalSelectorExtension } from './extensions/ClientPrincipalSelector';
import { ClientViewPage } from './extensions/ClientViewPage';
import { NavigateFromFolderToClientExtension } from './extensions/NavigateFromFolderToClient';
import { RecentlyDeletedPage } from './extensions/RecentlyDeletedPage';
import { ServiceTeamsPage } from './extensions/ServiceTeamsPage';
import { WorkflowsClientContactSelectorExtension } from './extensions/WorkflowsClientContactSelector';
import { FeatureFlag } from './featureFlags';
import { addOverlays } from './overlays';
import { AddServiceTeamMembersDrawer } from './overlays/AddServiceTeamMembersDrawer';
import { CreateCustomFieldsDrawer } from './overlays/CreateCustomFieldDrawer';
import { ServiceTeamDrawer } from './overlays/ServiceTeamDrawer';
import { UpdateCustomFieldsDrawer } from './overlays/UpdateCustomFieldDrawer';
import { ViewServiceTeamMembersDrawer } from './overlays/ViewServiceTeamMembersDrawer';
import {
	clientHubCustomFieldsPath,
	clientHubManagementPath,
	clientHubServiceTeamsPath,
} from './routes';
import { t } from './utils/translate';

const smokeTestCustomers = [
	'pilet-tests.sharefiletest.com',
	'pilet-tests.sharefilestaging.com',
	'pilet-tests.sharefile.com',
];

export function setup(piral: PiletApi) {
	// Initialize SF API configuration
	piral.sf.registerSfApiConfigHandler(config => Api.configure(config as any));

	const { user, account, entitlements } = piral.sf.piletUserContext.get();
	const clientMgtEnabled = account.Preferences?.EnableClientManagement ?? false;
	const isEntitledForClientManagement: boolean = entitlements.getEntitlementStatus(
		'sharefile:entitlementdef-client-mgt-create'
	);

	// Always register the domino type renderer
	piral.registerExtension(
		'urn:sfextslot:client-mgt-pilet:domino:client-contact:type',
		props => <WorkflowsClientContactSelectorExtension {...props} />
	);

	// Ensure that the AST does not disable Admin Setting entries or the extension in Client Settings area
	if (isEntitledForClientManagement) {
		piral.registerExtension(
			'urn:sfextslot:client-mgt-pilet:client-configuration-management',
			props => <AsyncClientManagementConfiguration {...props} />
		);

		registerAdminSettingsEntries(piral);
	}

	// If this logic is modified, ensure corresponding updates are made in FBP:
	// https://code.sharefile-coretools.com/projects/SF_SYSTEM/repos/sharefile-storage/browse/src/ShareFile.Services.Storage.Core/v3/NavigationResource/NavigationResourceService.cs#75
	if (!clientMgtEnabled || !isEntitledForClientManagement) {
		return;
	}

	addBlocks(piral);
	addOverlays(piral);

	const isUserClientType = user.Roles?.includes('Client');
	piral.sf.registerLeftNavComponent({
		href: '/clients',
		title: () => t('clientlist.heading.title'),
		icon: Users,
		weight: 50,
		isAvailable: () => !isUserClientType,
	});

	piral.registerPage('/clients/deleted', props => <RecentlyDeletedPage {...props} />);
	piral.registerPage('/clients/view/:clientRID', props => <ClientViewPage {...props} />);
	piral.registerPage('/clients', props => <ClientManagementHomePage {...props} />);

	registerExtensions(piral);

	piral.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'client',
		defaultPath: '/clients/view/{clientRID}',
		callback: async (rid: string) => {
			const clientRID = RID.parse(rid);
			return { clientRID };
		},
	});

	registerDeveloperTools(piral);
}

function registerExtensions(piral: PiletApi) {
	piral.registerExtension(
		'urn:sfextslot:client-mgt-pilet:assign-resource-to-client',
		props => <AssignResourceToClientExtension {...props} />
	);

	piral.registerExtension(
		'urn:sfextslot:client-mgt-pilet:navigate-from-folder-to-client',
		props => <NavigateFromFolderToClientExtension {...props} />
	);

	piral.registerExtension(
		'urn:sfextslot:client-mgt-pilet:assign-contact-to-client',
		props => <AssignContactToClientExtension {...props} />
	);

	piral.registerExtension(
		'urn:sfextslot:client-mgt-pilet:client-configuration-data-accessor',
		props => <ClientConfigurationDataAccessorExtension {...props} />
	);

	piral.registerExtension(
		'urn:sfextslot:client-mgt-pilet:client-contact-select',
		props => <ClientContactSelectorExtension {...props} />
	);

	piral.registerExtension(
		'urn:sfextslot:client-mgt-pilet:client-principal-select',
		props => <ClientPrincipalSelectorExtension {...props} />
	);

	piral.registerExtension(
		'urn:permissions-pilet:private-group:provider:clientmanagement',
		props => <ClientManagementGroupDataExtensionSlotComponent {...props} />
	);
}

function registerDeveloperTools(piral: PiletApi) {
	if (
		process.env.NODE_ENV === 'development' ||
		smokeTestCustomers.includes(location.hostname)
	) {
		addBlockDevelopment(piral);

		if (
			process.env.NODE_ENV === 'development' &&
			typeof piral.meta.config.apiOrigin === 'string'
		) {
			/**
			 * Manual require ensures tree-shaking
			 * does not include these modules in the entry index.js
			 */
			const { Configuration } = require('./generated/runtime');
			const { configureHttp } = require('./httpConfiguration');
			configureHttp((config: any) => {
				config.config = new Configuration({
					basePath: `${piral.meta.config.apiOrigin}/io/clientmgt/v1`,
				});
			});
		}
	}
}

function registerAdminSettingsEntries(piral: PiletApi) {
	const serviceTeamCreationEnabled =
		getFeatureFlagValue(FeatureFlag.EnableClientMgtServiceTeamCreation) ?? false;

	if (serviceTeamCreationEnabled) {
		piral.sf.registerLeftSecondaryNavChildComponent({
			parent: clientHubManagementPath,
			href: clientHubServiceTeamsPath,
			title: () => t('adminSettings.service_teams'),
		});
		piral.registerPage(clientHubServiceTeamsPath, props => (
			<ServiceTeamsPage {...props} />
		));
		piral.registerDrawer(
			'urn:sfdrawer:client-mgt-pilet:service-team:manage',
			props => {
				return <ServiceTeamDrawer {...props} />;
			},
			{
				drawerProps: {
					width: '600px',
				},
			}
		);
		piral.registerDrawer(
			'urn:sfdrawer:client-mgt-pilet:service-team:add-members',
			props => {
				return <AddServiceTeamMembersDrawer {...props} />;
			},
			{
				drawerProps: {
					width: '400px',
				},
			}
		);
		piral.registerDrawer(
			'urn:sfdrawer:client-mgt-pilet:service-team:view-members',
			props => {
				return <ViewServiceTeamMembersDrawer {...props} />;
			},
			{
				drawerProps: {
					width: '600px',
				},
			}
		);
	}

	piral.sf.registerLeftSecondaryNavChildComponent({
		parent: clientHubManagementPath,
		href: clientHubCustomFieldsPath,
		title: () => t('adminSettings.custom_fields'),
	});
	piral.registerPage(clientHubCustomFieldsPath, props => (
		<ClientConfigurationPage {...props} />
	));
	piral.registerDrawer(
		'urn:sfdrawer:client-mgt-pilet:custom-fields:create',
		props => {
			return <CreateCustomFieldsDrawer {...props} />;
		},
		{
			drawerProps: {
				width: '400px',
			},
		}
	);
	piral.registerDrawer(
		'urn:sfdrawer:client-mgt-pilet:custom-fields:update',
		props => {
			return <UpdateCustomFieldsDrawer {...props} />;
		},
		{
			drawerProps: {
				width: '400px',
			},
		}
	);
}
